import { loadStripe } from "@stripe/stripe-js";
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import Button from "../../../components/form/Button";
import { toast } from "react-toastify";
import useMutation from "../../../hooks/useMutation";
import { useState } from "react";
import useFetch from "../../../hooks/useFetch";
import { cardList } from "../../../services/PaymentService";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const SavedCardOption = ({ card, isSelected, onSelect }) => {
  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer transition-all ${
        isSelected 
          ? "border-blue-500 bg-blue-50" 
          : "border-gray-200 hover:border-gray-300"
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center space-x-3">
        <input
          type="radio"
          checked={isSelected}
          onChange={onSelect}
          className="h-4 w-4 text-blue-600"
        />
        <div className="flex items-center space-x-2">
          <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
            {card.card.brand.toUpperCase()}
          </div>
          <span className="font-medium">
            •••• •••• •••• {card.card.last4}
          </span>
          <span className="text-sm text-gray-500">
            {card.card.exp_month}/{card.card.exp_year}
          </span>
        </div>
      </div>
    </div>
  );
};

const CheckoutForm = ({ onSuccess, amount, currency, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [saveCard, setSaveCard] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentMode, setPaymentMode] = useState('new'); // 'existing' or 'new'

  const { data: cards, isLoading: cardLoading } = useFetch({
    dependencies: ["card-list"],
    fetchFunction: cardList
  });

  const { mutate: handleSubmit, isLoading } = useMutation({
    mutationFn: async () => {
      if (!stripe) {
        throw new Error("Stripe is not loaded.");
      }

      let paymentIntent;

      if (paymentMode === 'existing' && selectedPaymentMethod) {
        // Use existing card
        const { error, paymentIntent: intent } = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            payment_method: selectedPaymentMethod,
            return_url: window.location.origin,
          },
          redirect: "if_required",
        });

        if (error) {
          throw new Error(error.message);
        }
        paymentIntent = intent;
      } else if (paymentMode === 'new') {
        // Use new card from PaymentElement
        if (!elements) {
          throw new Error("Payment form is not loaded.");
        }

        const { error: submitError } = await elements.submit();
        if (submitError) {
          throw new Error(submitError.message);
        }

        const { error, paymentIntent: intent } = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
          confirmParams: {
            return_url: window.location.origin,
            payment_method_data: {
              billing_details: {
                address: {
                  country: 'US'
                }
              }
            }
          },
        });

        if (error) {
          throw new Error(error.message);
        }
        paymentIntent = intent;
      } else {
        throw new Error("Please select a payment method.");
      }

      if (paymentIntent?.status === "succeeded") {
        setPaymentIntentId(paymentIntent.id);
        onSuccess?.(paymentIntent, paymentMode === 'new' ? saveCard : false);
      }

      return paymentIntent;
    },
    onSuccess: () => {
      toast.success("Payment successful!");
      setSaveCard(false);
    },
    onError: (error) => {
      toast.error(error.message || "Payment failed.");
    },
  });

  const paymentElementOptions = {
    fields: {
      billingDetails: {
        address: {
          country: 'never'
        }
      }
    }
  };

  // Show loading state while cards are being fetched
  if (cardLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 flex flex-col">
      {!paymentIntentId ? (
        <>
          {/* Payment Method Selection */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900">Select Payment Method</h3>
            
            {/* Existing Cards */}
            {cards?.data && cards.data.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Saved Cards</h4>
                {cards.data.map((card) => (
                  <SavedCardOption
                    key={card.id}
                    card={card}
                    isSelected={paymentMode === 'existing' && selectedPaymentMethod === card.id}
                    onSelect={() => {
                      setPaymentMode('existing');
                      setSelectedPaymentMethod(card.id);
                    }}
                  />
                ))}
              </div>
            )}

            {/* Add New Card Option */}
            <div
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                paymentMode === 'new'
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => {
                setPaymentMode('new');
                setSelectedPaymentMethod(null);
              }}
            >
              <div className="flex items-center space-x-3">
                <input
                  type="radio"
                  checked={paymentMode === 'new'}
                  onChange={() => {
                    setPaymentMode('new');
                    setSelectedPaymentMethod(null);
                  }}
                  className="h-4 w-4 text-blue-600"
                />
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-5 bg-gray-400 rounded text-white text-xs flex items-center justify-center">
                    +
                  </div>
                  <span className="font-medium">Add New Card</span>
                </div>
              </div>
            </div>
          </div>

          {/* Show PaymentElement only when "Add New Card" is selected */}
          {paymentMode === 'new' && (
            <div className="space-y-3 pt-4 border-t">
              <PaymentElement options={paymentElementOptions} />
              
              {/* Save card checkbox - only show for new cards */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="save-card"
                  checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label 
                  htmlFor="save-card" 
                  className="text-sm text-gray-700 cursor-pointer"
                >
                  Save card for later use
                </label>
              </div>
            </div>
          )}

          {/* Payment Button */}
          <Button
            buttonFunction={handleSubmit}
            disabled={
              !stripe || 
              isLoading || 
              !paymentMode || 
              (paymentMode === 'new' && !elements)
            }
            buttonClasses={"ml-auto"}
            buttonLabel={
              isLoading
                ? "Processing..."
                : `Pay ${currency ? currency + " " : "$"}${amount}`
            }
          />
        </>
      ) : (
        <div className="text-center mt-6 space-y-4">
          <h2 className="text-2xl font-semibold text-green-600">
            Thank you for your payment! 🎉
          </h2>
          <p className="text-slate-600">
            Your payment was successful. You can now view your recent
            transactions or active plan.
          </p>
          <p className="text-slate-500 text-sm">
            <strong>Payment Intent ID:</strong> {paymentIntentId}
          </p>
        </div>
      )}
    </div>
  );
};

const StripePayment = ({
  clientSecret,
  intentId,
  amount,
  currency,
  onSuccess,
}) => {
  const [, setIsSuccess] = useState(false);
  const [, setIntentDetails] = useState(null);

  const options = {
    clientSecret,
    appearance: {
      theme: "flat",
    },
  };

  return (
    <div>
      {clientSecret && (
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm
            onSuccess={(intent, saveCardOption) => {
              onSuccess?.(intent, saveCardOption);
              setIsSuccess(true);
              setIntentDetails(intent);
            }}
            amount={amount}
            currency={currency}
            clientSecret={clientSecret}
          />
        </Elements>
      )}
    </div>
  );
};

export default StripePayment;